/**
 * El visor de fotos del chat interno (24/9/2026): la foto a pantalla
 * completa, sobre fondo oscuro, como en WhatsApp.
 *
 *  · Se cierra con la X, con Esc o deslizando hacia abajo.
 *  · Se pasa entre las fotos del MISMO mensaje con las flechas (en pantalla
 *    y del teclado) o deslizando a los lados.
 *  · Se descarga con el botón de bajar: la dirección lleva la sesión, igual
 *    que la foto; sin sesión, el servidor no la da.
 *  · Si la foto no carga (la han eliminado, se ha cortado la conexión), lo
 *    dice y deja reintentar: nada de una pantalla negra sin más.
 *
 * Es un diálogo de verdad, el de Radix de la casa (components/ui/dialog)
 * (supervisor, 24/9/2026: el primero era un <div> con role="dialog"): el foco
 * se queda DENTRO mientras está abierto, la página de detrás no se desplaza
 * ni la lee el lector de pantalla, Esc lo cierra y, al cerrarlo, el foco
 * vuelve a la foto que se pulsó.
 */
import React, { useEffect, useRef, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X, ChevronLeft, ChevronRight, Download, ImageOff, RotateCcw } from 'lucide-react';
import { Dialog, DialogPortal, DialogOverlay, DialogTitle } from '../ui/dialog';
import { urlFoto } from '../../lib/fotosChat';

// Cuánto hay que deslizar el dedo para que cuente (px).
const DESLIZAR = 50;

export default function VisorFotos({ sala, fotos, inicio = 0, autor, onCerrar }) {
  const [indice, setIndice] = useState(Math.min(Math.max(0, inicio), (fotos?.length || 1) - 1));
  // La foto que no ha cargado y cuántas veces se ha reintentado (para pedirla
  // otra vez con otra dirección y que el navegador no se quede con el fallo).
  const [fallo, setFallo] = useState(null);
  const [intento, setIntento] = useState(0);
  const cerrarRef = useRef(null);
  const toqueRef = useRef(null);
  // Lo que tenía el foco al abrir (la foto pulsada): ahí vuelve al cerrar.
  const previoRef = useRef(typeof document !== 'undefined' ? document.activeElement : null);
  const total = fotos?.length || 0;
  const foto = total ? fotos[indice] : null;

  const anterior = () => setIndice((i) => (i > 0 ? i - 1 : i));
  const siguiente = () => setIndice((i) => (i < total - 1 ? i + 1 : i));

  // Las flechas del teclado. Esc lo lleva el diálogo.
  useEffect(() => {
    const alTeclear = (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); setIndice((i) => (i > 0 ? i - 1 : i)); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); setIndice((i) => (i < total - 1 ? i + 1 : i)); }
    };
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, [total]);

  if (!foto) return null;

  const alTocar = (e) => {
    const t = e.touches?.[0];
    toqueRef.current = t ? { x: t.clientX, y: t.clientY } : null;
  };
  const alSoltar = (e) => {
    const desde = toqueRef.current;
    const t = e.changedTouches?.[0];
    toqueRef.current = null;
    if (!desde || !t) return;
    const dx = t.clientX - desde.x;
    const dy = t.clientY - desde.y;
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > DESLIZAR) {
      if (dx < 0) siguiente(); else anterior();
    } else if (dy > DESLIZAR * 2) {
      onCerrar();
    }
  };

  const noCarga = fallo === foto.id;
  const base = urlFoto(sala, foto.id);
  const src = intento ? `${base}${base.includes('?') ? '&' : '?'}r=${intento}` : base;
  const botonRedondo = 'inline-flex items-center justify-center w-11 h-11 rounded-full text-white/90 hover:text-white hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white';

  return (
    <Dialog open onOpenChange={(abierto) => { if (!abierto) onCerrar(); }}>
      <DialogPortal>
        <DialogOverlay className="z-[80] bg-black" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          // El foco, a «Cerrar» (lo primero que se busca con el teclado), y al
          // cerrar, de vuelta a la foto que se pulsó.
          onOpenAutoFocus={(e) => { e.preventDefault(); cerrarRef.current?.focus(); }}
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            const previo = previoRef.current;
            if (previo && previo.isConnected && typeof previo.focus === 'function') previo.focus();
          }}
          className="fixed inset-0 z-[80] flex flex-col bg-black text-white select-none outline-none"
          data-testid="chat-visor"
          onTouchStart={alTocar} onTouchEnd={alSoltar}>
          <DialogTitle className="sr-only">
            {`Foto ${indice + 1} de ${total}${autor ? `, de ${autor}` : ''}`}
          </DialogTitle>
          <div className="flex items-center gap-2 px-2 sm:px-4 pt-[max(0.5rem,env(safe-area-inset-top))] pb-2">
            <div className="flex-1 min-w-0 text-sm truncate">
              {autor ? <span className="font-medium">{autor}</span> : null}
              {total > 1 && (
                <span className="text-white/70 ml-2 tabular-nums" data-testid="chat-visor-cuenta">
                  {indice + 1} / {total}
                </span>
              )}
            </div>
            <a href={urlFoto(sala, foto.id, { descargar: true })} download
              className={botonRedondo} title="Descargar" aria-label="Descargar la foto"
              data-testid="chat-visor-descargar">
              <Download className="w-5 h-5" />
            </a>
            <button ref={cerrarRef} type="button" onClick={onCerrar} className={botonRedondo}
              title="Cerrar (Esc)" aria-label="Cerrar" data-testid="chat-visor-cerrar">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="relative flex-1 min-h-0 flex items-center justify-center px-2 sm:px-14 pb-[max(1rem,env(safe-area-inset-bottom))]"
            onClick={(e) => { if (e.target === e.currentTarget) onCerrar(); }}>
            {noCarga ? (
              <div role="alert" data-testid="chat-visor-error"
                className="max-w-xs text-center text-sm text-white/85 flex flex-col items-center gap-3">
                <ImageOff className="w-10 h-10 text-white/60" aria-hidden="true" />
                <div>
                  No se ha podido cargar la foto. Puede que la hayan eliminado o que se
                  haya cortado la conexión.
                </div>
                <button type="button"
                  onClick={() => { setFallo(null); setIntento((n) => n + 1); }}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/40 px-4 py-2 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                  data-testid="chat-visor-reintentar">
                  <RotateCcw className="w-4 h-4" aria-hidden="true" /> Reintentar
                </button>
              </div>
            ) : (
              <img key={`${foto.id}:${intento}`} src={src} alt={`Foto ${indice + 1} de ${total}`}
                draggable={false}
                onError={() => setFallo(foto.id)}
                className="max-w-full max-h-full object-contain rounded-sm"
                data-testid="chat-visor-foto" />
            )}
            {total > 1 && indice > 0 && (
              <button type="button" onClick={anterior}
                className={`${botonRedondo} absolute left-1 sm:left-3 top-1/2 -translate-y-1/2 bg-black/40`}
                aria-label="Foto anterior" data-testid="chat-visor-anterior">
                <ChevronLeft className="w-7 h-7" />
              </button>
            )}
            {total > 1 && indice < total - 1 && (
              <button type="button" onClick={siguiente}
                className={`${botonRedondo} absolute right-1 sm:right-3 top-1/2 -translate-y-1/2 bg-black/40`}
                aria-label="Foto siguiente" data-testid="chat-visor-siguiente">
                <ChevronRight className="w-7 h-7" />
              </button>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
